import React, { useState } from 'react';
import { Brand } from '../components/Brand';
import { Button, Card } from '../components/Primitives';
import { useApp } from '../lib/app-context';
import {
  cloudConfigured,
  signIn,
  signInWithGoogle,
  signUp,
} from '../lib/supabase';

type TelegramLoginResult = {
  id_token?: string;
  error?: string;
  user?: unknown;
};

async function loadTelegramLoginLibrary() {
  const telegram =
    (window as any).Telegram;

  if (
    telegram?.Login?.auth
  ) {
    return;
  }

  await new Promise<void>(
    (resolve, reject) => {
      const existing =
        document.querySelector<HTMLScriptElement>(
          'script[data-ef-telegram-login]',
        );

      if (existing) {
        existing.addEventListener(
          'load',
          () => resolve(),
          { once: true },
        );

        existing.addEventListener(
          'error',
          () =>
            reject(
              new Error(
                'Telegram Login kutubxonasi yuklanmadi.',
              ),
            ),
          { once: true },
        );

        return;
      }

      const script =
        document.createElement('script');

      script.src =
        'https://telegram.org/js/telegram-login.js';

      script.async = true;

      script.dataset.efTelegramLogin =
        '1';

      script.onload =
        () => resolve();

      script.onerror =
        () =>
          reject(
            new Error(
              'Telegram Login kutubxonasi yuklanmadi.',
            ),
          );

      document.head.appendChild(
        script,
      );
    },
  );
}

async function telegramWebLogin() {
  const clientId = Number(
    import.meta.env
      .VITE_TELEGRAM_CLIENT_ID,
  );

  const supabaseUrl =
    import.meta.env
      .VITE_SUPABASE_URL as
      | string
      | undefined;

  const publishableKey =
    import.meta.env
      .VITE_SUPABASE_PUBLISHABLE_KEY as
      | string
      | undefined;

  const publicAppUrl =
    import.meta.env
      .VITE_PUBLIC_APP_URL as
      | string
      | undefined;

  if (publicAppUrl) {
    try {
      const canonicalOrigin =
        new URL(publicAppUrl).origin;

      const isCloudflarePreview =
        window.location.hostname.endsWith(
          '.english-forge-365.pages.dev',
        ) &&
        window.location.origin !==
          canonicalOrigin;

      if (isCloudflarePreview) {
        throw new Error(
          `Telegram Login xavfsizlik sabab asosiy domen orqali ishlaydi: ${canonicalOrigin}/login`,
        );
      }
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Telegram Login')) {
        throw error;
      }
    }
  }

  if (
    !Number.isFinite(clientId) ||
    clientId <= 0
  ) {
    throw new Error(
      'Telegram Client ID sozlanmagan.',
    );
  }

  if (
    !supabaseUrl ||
    !publishableKey
  ) {
    throw new Error(
      'Cloud backend sozlanmagan.',
    );
  }

  await loadTelegramLoginLibrary();

  const telegram =
    (window as any).Telegram;

  if (
    !telegram?.Login?.auth
  ) {
    throw new Error(
      'Telegram Login ishga tushmadi.',
    );
  }

  return new Promise<void>(
    (resolve, reject) => {
      let settled = false;
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        fn();
      };
      const timeout = window.setTimeout(() => {
        finish(() => reject(new Error('Telegram Login oynasi javob bermadi. Allowed URL va popup sozlamalarini tekshiring.')));
      }, 90_000);

      telegram.Login.auth(
        {
          client_id: clientId,
          scope: ['profile'],
          lang: 'uz',
        },

        async (
          result: TelegramLoginResult,
        ) => {
          try {
            if (!result) {
              throw new Error(
                'Telegram login bekor qilindi.',
              );
            }

            if (result.error) {
              throw new Error(
                result.error,
              );
            }

            if (!result.id_token) {
              throw new Error(
                'Telegram ID token olinmadi.',
              );
            }

            const response =
              await fetch(
                `${supabaseUrl}/functions/v1/telegram-auth`,
                {
                  method: 'POST',

                  headers: {
                    'content-type':
                      'application/json',

                    apikey:
                      publishableKey,
                  },

                  body:
                    JSON.stringify({
                      idToken:
                        result.id_token,
                    }),
                },
              );

            const data =
              await response.json();

            if (!response.ok) {
              if (data?.code === 'TELEGRAM_LOGIN_CLIENT_ID_MISSING') {
                throw new Error('Telegram Login server Client ID sozlanmagan.');
              }
              throw new Error(
                data?.error ||
                  'Telegram orqali kirib bo‘lmadi.',
              );
            }

            if (!data?.sessionToken) {
              throw new Error(
                'Telegram sessiyasi yaratilmadi.',
              );
            }

            localStorage.setItem(
              'ef-telegram-session',
              data.sessionToken,
            );

            finish(resolve);
          } catch (error) {
            finish(() => reject(error));
          }
        },
      );
    },
  );
}

export function Login() {
  const { setRoute } =
    useApp();

  const [
    mode,
    setMode,
  ] =
    useState<
      'login' | 'signup'
    >('login');

  const [
    firstName,
    setFirstName,
  ] =
    useState('');

  const [
    lastName,
    setLastName,
  ] =
    useState('');

  const [
    email,
    setEmail,
  ] =
    useState('');

  const [
    password,
    setPassword,
  ] =
    useState('');

  const [
    confirmPassword,
    setConfirmPassword,
  ] =
    useState('');

  const [
    status,
    setStatus,
  ] =
    useState('');

  const [
    busy,
    setBusy,
  ] =
    useState(false);

  const continueToApp = () => {
    window.location.assign(
      '/app',
    );
  };

  const googleLogin =
    async () => {
      if (busy) return;

      setBusy(true);
      setStatus(
        'Google ochilmoqda...',
      );

      try {
        const result =
          await signInWithGoogle();

        if (result.error) {
          throw result.error;
        }

        // signInWithOAuth browserni
        // Google sahifasiga redirect qiladi.
      } catch (error) {
        setStatus(
          error instanceof Error
            ? error.message
            : 'Google login xatosi.',
        );

        setBusy(false);
      }
    };

  const telegramLogin =
    async () => {
      if (busy) return;

      setBusy(true);

      setStatus(
        'Telegram ochilmoqda...',
      );

      try {
        await telegramWebLogin();

        setStatus(
          'Telegram orqali kirildi.',
        );

        continueToApp();
      } catch (error) {
        setStatus(
          error instanceof Error
            ? error.message
            : 'Telegram login xatosi.',
        );

        setBusy(false);
      }
    };

  const submit =
    async (
      e: React.FormEvent,
    ) => {
      e.preventDefault();

      if (!cloudConfigured) {
        setStatus(
          'Cloud backend sozlanmagan.',
        );

        return;
      }

      if (
        mode === 'signup'
      ) {
        if (
          !firstName.trim() ||
          !lastName.trim()
        ) {
          setStatus(
            'Ism va familiyani kiriting.',
          );

          return;
        }

        if (
          password !==
          confirmPassword
        ) {
          setStatus(
            'Parollar bir xil emas.',
          );

          return;
        }
      }

      setBusy(true);
      setStatus(
        'Kutilmoqda...',
      );

      try {
        if (
          mode === 'login'
        ) {
          const result =
            await signIn(
              email,
              password,
            );

          if (result.error) {
            throw result.error;
          }

          continueToApp();

          return;
        }

        const result =
          await signUp(
            email,
            password,
            {
              firstName,
              lastName,
            },
          );

        if (result.error) {
          throw result.error;
        }

        if (
          result.data.session
        ) {
          continueToApp();

          return;
        }

        setStatus(
          'Account yaratildi. Email tasdiqlash yoqilgan bo‘lsa, pochtangizdagi havolani bosing.',
        );
      } catch (error) {
        setStatus(
          error instanceof Error
            ? error.message
            : 'Kirish xatosi.',
        );
      } finally {
        setBusy(false);
      }
    };

  return (
    <div className="auth-page">
      <div className="auth-box">
        <Brand />

        <Card>
          <span className="eyebrow">
            ENGLISH FORGE 365
          </span>

          <h1>
            {mode === 'login'
              ? 'Kirish'
              : 'Ro‘yxatdan o‘tish'}
          </h1>

          <p>
            O‘zingizga qulay usul
            orqali davom eting.
          </p>

          <div
            style={{
              display: 'grid',
              gap: 10,
              marginBottom: 18,
            }}
          >
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void googleLogin()
              }
            >
              G · Google orqali davom etish
            </Button>

            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void telegramLogin()
              }
            >
              ✈ · Telegram orqali davom etish
            </Button>
          </div>

          <div
            style={{
              textAlign: 'center',
              opacity: 0.65,
              margin: '12px 0 18px',
            }}
          >
            yoki email orqali
          </div>

          <form
            onSubmit={submit}
          >
            {mode ===
              'signup' && (
              <>
                <label>
                  Ism

                  <input
                    value={
                      firstName
                    }
                    onChange={(e) =>
                      setFirstName(
                        e.target
                          .value,
                      )
                    }
                    autoComplete="given-name"
                    required
                  />
                </label>

                <label>
                  Familiya

                  <input
                    value={
                      lastName
                    }
                    onChange={(e) =>
                      setLastName(
                        e.target
                          .value,
                      )
                    }
                    autoComplete="family-name"
                    required
                  />
                </label>
              </>
            )}

            <label>
              Email

              <input
                type="email"
                value={email}
                onChange={(e) =>
                  setEmail(
                    e.target.value,
                  )
                }
                autoComplete="email"
                required
              />
            </label>

            <label>
              Parol

              <input
                type="password"
                value={password}
                onChange={(e) =>
                  setPassword(
                    e.target.value,
                  )
                }
                minLength={8}
                autoComplete={
                  mode ===
                  'login'
                    ? 'current-password'
                    : 'new-password'
                }
                required
              />
            </label>

            {mode ===
              'signup' && (
              <label>
                Parolni tasdiqlang

                <input
                  type="password"
                  value={
                    confirmPassword
                  }
                  onChange={(e) =>
                    setConfirmPassword(
                      e.target
                        .value,
                    )
                  }
                  minLength={8}
                  autoComplete="new-password"
                  required
                />
              </label>
            )}

            <Button
              type="submit"
              disabled={busy}
            >
              {busy
                ? 'Kutilmoqda...'
                : mode ===
                    'login'
                  ? 'Email orqali kirish'
                  : 'Account yaratish'}
            </Button>
          </form>

          {status && (
            <div className="status-message">
              {status}
            </div>
          )}

          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => {
              setStatus('');

              setMode(
                mode === 'login'
                  ? 'signup'
                  : 'login',
              );
            }}
          >
            {mode === 'login'
              ? 'Email orqali yangi account yaratish'
              : 'Menda account bor'}
          </button>

          <button
            type="button"
            className="text-button"
            onClick={() =>
              setRoute(
                'landing',
              )
            }
          >
            Bosh sahifaga qaytish
          </button>
        </Card>
      </div>
    </div>
  );
}
